import { ICalendarItemData } from 'features/appointment/services/types/calendar';
import React, { useMemo, useState } from 'react';
import DraggableItem from '../Calendar/TableView/Body/DnD/DraggableItem';
import styled from 'styled-components';
import { IAppointmentItemData } from 'features/appointment/services/types/appointment';
import Text from 'components/Text';
import { momentTimezone } from 'utils/time';
import { FORMAT_TIME } from 'features/appointment/utils/format';
import { Row } from 'antd';
import { formatPhone } from 'utils/formatPhone';
import storage from 'utils/sessionStorage';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store/hooks';
import appointmentActions from 'features/appointment/services/actions';
import ic_delete from './ic_delete.svg';
import RequestAdminPWCallback, { useRequestAdminPWCallbackRef } from 'widgets/RequestAdminLocalPW/Callback';
import appointmentApis from 'features/appointment/services/apis';
import { IResponseDataBody } from 'services/response';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import Box from 'components/Box';
import Icon from 'assets/Icons';
import anybodyImg from 'assets/icon-anybody.png';
import ModalChooseStaffRef, { useModalChooseStaffRef } from 'widgets/Staff/ModalChooseStaffRef';
import toast from 'utils/toast';
import appointmentSelectors from 'features/appointment/services/selectors';
import colorTheme from 'constants/color';

type Props = {
  anybodyList: ICalendarItemData[];
};
const AnybodyList = ({ anybodyList }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const pinCodeRef = useRequestAdminPWCallbackRef();
  const setLoadingPage = useSetLoadingPage();
  const modalChooseStaffRef = useModalChooseStaffRef();
  const [loading, setLoading] = useState(false);

  const staffFilter = appointmentSelectors.staffFilter();
  const staffListStore = appointmentSelectors.getListStaffAvailableBooking();

  const staffList = useMemo(() => {
    return staffFilter ? [staffFilter, ...staffListStore.filter((s) => s.id !== staffFilter.id)] : staffListStore;
  }, [staffListStore, staffFilter]);

  const onDoubleClick = (data: ICalendarItemData) => {
    navigate(`/store/${storage.shop_id.get()}/appointments/${data?.id || ''}`);
    dispatch(appointmentActions.setAppointmentDetail(data.data as IAppointmentItemData));
  };

  const onDelete = (data: ICalendarItemData) =>
    pinCodeRef.current?.open(async () => {
      if (!data?.id) return;

      setLoadingPage(true);
      try {
        const res: IResponseDataBody<boolean> = await appointmentApis.deleteAppointment(data?.id);
        if (res.data.data) {
          dispatch(appointmentActions.deleteAppointment.success(res.data.data));
          dispatch(
            appointmentActions.anybodyBox.setAnybodyAppointmentList(anybodyList.filter((o) => o.id !== data.id)),
          );
        }
      } catch (error) {
      } finally {
        setLoadingPage(false);
      }
    });

  const updateStaffForAppointment = async (appointment: ICalendarItemData, staffId: string) => {
    if (!appointment?.id || !staffId) return;
    setLoading(true);
    try {
      const res: IResponseDataBody<boolean> = await appointmentApis.updateStaffMultipleAppointment({
        appointmentId: appointment.id,
        staffId,
      });

      if (res?.data?.data) {
        toast.success('Update staff for appointment successfully');

        dispatch(
          appointmentActions.updateAppointmentWithDrop({
            appointmentId: appointment.id,
            staffId,
            startTime: appointment.startTime,
          }),
        );

        dispatch(
          appointmentActions.anybodyBox.setAnybodyAppointmentList(anybodyList.filter((o) => o.id !== appointment.id)),
        );
      }
    } catch {
      toast.error('Failed to update staff for appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStaff = (appointment: ICalendarItemData) => {
    modalChooseStaffRef.current?.open((staff) => {
      updateStaffForAppointment(appointment, staff.id);
    });
  };

  return (
    <>
      <Container wrap={false}>
        {anybodyList.map((o) => (
          <AnybodyTicketItem
            key={o.id}
            data={o}
            onDoubleClick={() => onDoubleClick(o)}
            onDelete={() => onDelete(o)}
            onChangeStaff={() => handleChangeStaff(o)}
          />
        ))}
      </Container>
      <RequestAdminPWCallback onlyAdmin v2 ref={pinCodeRef} />
      <ModalChooseStaffRef anybody loading={loading} staffs={staffList} v2 ref={modalChooseStaffRef} noneSearch />
    </>
  );
};

export default AnybodyList;

const Container = styled(Row)`
  overflow-x: scroll;
  gap: 18px;
  padding: 1rem 1rem;
  padding-bottom: 8px;
  zoom: 1.4;
`;

type AnyTicketItemProps = {
  data: ICalendarItemData;
  onDoubleClick?: () => void;
  onDelete?: () => void;
  onChangeStaff?: () => void;
};
export const AnybodyTicketItem = ({
  data,
  onDoubleClick = () => undefined,
  onDelete = () => undefined,
  onChangeStaff = () => undefined,
}: AnyTicketItemProps) => {
  const extraData = data.data as IAppointmentItemData;

  const time = useMemo(() => {
    const startTime = momentTimezone(data.startTime);
    const endTime = momentTimezone(data.endTime);
    return `${startTime.format(FORMAT_TIME)} - ${endTime.format(FORMAT_TIME)}`;
  }, [data]);

  return (
    <AnyTicketItemStyled onDoubleClick={onDoubleClick}>
      <DraggableItem key={data?.id} id={data?.id?.toString()} data={{ data }}>
        <Box display='flex' flexDirection='column' alignItems='start' height='100%' justifyContent='space-between'>
          <Box display='flex' flexDirection='column' width='100%'>
            <Text variant='CAPTION_2' color='v2_secondary_3' style={{ whiteSpace: 'nowrap', fontSize: '12px' }} textAlign='left'>
              {time}
            </Text>
            <Box display='flex' flexDirection='column'>
              <Text variant='CAPTION_2' color='text_3' style={{ whiteSpace: 'nowrap', lineHeight: '1' }} textAlign='left'>
                {extraData.customerName || 'Customer name'}
              </Text>
              <Text
                variant='CAPTION_4'
                color='text_4_v2'
                style={{ whiteSpace: 'nowrap', fontWeight: 500 }}
                textAlign='left'
              >
                {formatPhone(extraData?.customerPhone || '')}
              </Text>
            </Box>
            <Text variant='CAPTION_4' color='text_3' textAlign='left' className='service-text'>
              {extraData.services?.map((o) => `${o.serviceName}`).join(', ')}
            </Text>
          </Box>
          <ButtonChangeStaff
            onClick={(e: any) => {
              e.stopPropagation();
              onChangeStaff();
            }}
          >
            <Box display='flex' alignItems='center' justifyContent='center' gap='1'>
              <img src={anybodyImg} alt='anybodyImg' style={{ width: 16, height: 16, borderRadius: 48 }} />
              <Text variant='CAPTION_3' style={{ fontWeight: 500 }}>
                {extraData.staffName}
              </Text>
            </Box>

            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              width='16px'
              height='16px'
              borderRadius='4px'
              bgColor='v2_secondary_1'
            >
              <Icon type='changeCustomer' fill='#6FABB6' width={12} height={12} />
            </Box>
          </ButtonChangeStaff>
        </Box>
      </DraggableItem>
      <button type='button' className='delete-button' onClick={(e) => [e.stopPropagation(), onDelete()]}>
        <img src={ic_delete} alt='ic_delete' style={{ width: 16, height: 16 }} />
      </button>
    </AnyTicketItemStyled>
  );
};

const AnyTicketItemStyled = styled.div`
  border-radius: 5px;
  border: 1px dashed ${colorTheme.v2_secondary_3};
  background: ${colorTheme.v2_secondary_5};
  padding: 4px 8px;
  position: relative;
  .DraggableStyled {
    position: relative;
  }
  .delete-button {
    position: absolute;
    right: -16px;
    top: -16px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #64748B;
    border-radius: 100%;
  }

  .service-text {
    margin: 2px 0;
    max-width: 120px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

const ButtonChangeStaff = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  background: transparent;
  gap: 4px;
  padding-top: 2px;
  border-top: 1px solid ${colorTheme.v2_secondary_4};
`;
